import { api } from './api';

export type DocumentCategory =
  | 'quote'
  | 'kbis'
  | 'financial_statement'
  | 'certificate'
  | 'rib'
  | 'other';

export interface DocumentItem {
  id: string;
  prospectId: string;
  opportunityId: string | null;
  category: DocumentCategory;
  checklistItemId: string | null;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedBy: string;
  createdAt: string;
  updatedAt: string;
  prospect: {
    id: string;
    type: 'company' | 'individual';
    companyName: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  opportunity: { id: string; reference: string; stage: string } | null;
  checklistItem: { id: string; documentName: string; isRequired: boolean } | null;
  uploader: { id: string; firstName: string; lastName: string; email: string };
}

export interface DocumentFilters {
  prospect_id?: string;
  opportunity_id?: string;
  category?: DocumentCategory;
  cursor?: string;
  limit?: number;
}

export interface UploadDocumentPayload {
  prospectId: string;
  opportunityId?: string;
  category: DocumentCategory;
  checklistItemId?: string;
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

function auth(token: string) {
  return { accessToken: token };
}

export const documentsApi = {
  list: (token: string, filters: DocumentFilters = {}) =>
    api.get<{ data: DocumentItem[]; nextCursor: string | null }>(
      `/api/v1/documents${qs(filters as Record<string, unknown>)}`,
      auth(token),
    ),

  upload: async (
    token: string,
    file: File,
    payload: UploadDocumentPayload,
  ): Promise<DocumentItem> => {
    const form = new FormData();
    form.append('prospectId', payload.prospectId);
    if (payload.opportunityId) form.append('opportunityId', payload.opportunityId);
    form.append('category', payload.category);
    if (payload.checklistItemId) form.append('checklistItemId', payload.checklistItemId);
    form.append('file', file);
    const res = await fetch('/api/v1/documents', {
      method: 'POST',
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const err = (data as { error?: { code: string; message: string } } | null)?.error;
      throw new Error(err?.message ?? 'Upload failed');
    }
    return data as DocumentItem;
  },

  delete: (token: string, id: string) => api.delete<void>(`/api/v1/documents/${id}`, auth(token)),

  downloadUrl: (token: string, id: string) =>
    api.get<{ url: string; expiresInSeconds: number; fileName: string }>(
      `/api/v1/documents/${id}/download`,
      auth(token),
    ),
};

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  quote: 'Devis',
  kbis: 'Kbis',
  financial_statement: 'Bilan',
  certificate: 'Attestation',
  rib: 'RIB',
  other: 'Autre',
};

export const DOCUMENT_CATEGORY_TONES: Record<
  DocumentCategory,
  'neutral' | 'accent' | 'success' | 'warning' | 'danger'
> = {
  quote: 'accent',
  kbis: 'neutral',
  financial_statement: 'neutral',
  certificate: 'success',
  rib: 'neutral',
  other: 'neutral',
};
